import React, { useState, useMemo } from 'react';
import HabitCalendarConflictModal from './HabitCalendarConflictModal';
import { Check, ChevronRight, ChevronLeft, X, Clock, Zap, Info, Gift, Plus, Trash2, Layers } from 'lucide-react';
import { getLocalDateStr, canCreateNewHabit } from '../../lib/habitUtils';
import SchichtRhythmusModal, { LS_SCHICHT_KEY } from './SchichtRhythmusModal';
import SchichtTagsModal from './SchichtTagsModal';
import SchichtVorschauModal from './SchichtVorschauModal';
import { userStorage } from '../../utils/userStorage';
const CUE_PRESETS = [
  'Nach dem Aufstehen', 'Nach dem Kaffee', 'Nach dem Mittagessen',
  'Vor dem Schlafen', 'Nach dem Duschen', 'In der Mittagspause',
  'Nach der Arbeit', 'Morgens um 7'
];

const WEEKDAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const SCHICHT_TYPES = ['Früh', 'Spät', 'Nacht', 'Frei'];
const SCHICHT_BTN_ACTIVE = {
  Früh:  'bg-orange-100 text-orange-800 border-orange-300',
  Spät:  'bg-blue-100 text-blue-800 border-blue-300',
  Nacht: 'bg-purple-100 text-purple-800 border-purple-300',
  Frei:  'bg-teal-100 text-teal-700 border-teal-300',
};
const DEFAULT_ZEITEN = {
  Früh:  { enabled: false, type: 'alle', calendarBlock: false, time: '16:00', duration: 30, months: 3 },
  Spät:  { enabled: false, type: 'alle', calendarBlock: false, time: '09:00', duration: 30, months: 3 },
  Nacht: { enabled: false, type: 'alle', calendarBlock: false, time: '13:00', duration: 30, months: 3 },
  Frei:  { enabled: false, type: 'alle', calendarBlock: false, time: '10:00', duration: 30, months: 3 },
};

export default function HabitAddWizard({ habits, onSave, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [type, setType] = useState(null);
  const [title, setTitle] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [weeklyTarget, setWeeklyTarget] = useState(3);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState('08:00');
  const [calendarEnabled, setCalendarEnabled] = useState(false);
  const [calendarDuration, setCalendarDuration] = useState(15);
  const [consolidationMonths, setConsolidationMonths] = useState(3);
  const [showConsolidationInfo, setShowConsolidationInfo] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictModalForShift, setConflictModalForShift] = useState(null);
  const [pendingSkippedDates, setPendingSkippedDates] = useState([]);
  const [pendingDailyTimeOverrides, setPendingDailyTimeOverrides] = useState({});
  const [conflictsFullyResolved, setConflictsFullyResolved] = useState(false);
  const [resolvedShifts, setResolvedShifts] = useState(new Set());
  const [pendingAptChanges, setPendingAptChanges] = useState({ updates: {}, deletes: [] });
  const [minimumTarget, setMinimumTarget] = useState(1);
  const [currentTarget, setCurrentTarget] = useState(1);
  const [goalTarget, setGoalTarget] = useState('');
  const [unitLabel, setUnitLabel] = useState('');
  const [cue, setCue] = useState('');
  // Schichten
  const [schichtRhythmus, setSchichtRhythmus] = useState(null);
  const [schichtZeiten, setSchichtZeiten] = useState({ ...DEFAULT_ZEITEN });
  const [showSchichtModal, setShowSchichtModal] = useState(false);
  const [showSchichtTagsModal, setShowSchichtTagsModal] = useState(false);
  const [schichtTagsTargetSchicht, setSchichtTagsTargetSchicht] = useState(null);
  const [showVorschauModal, setShowVorschauModal] = useState(false);
  // Rewards
  const [rewardRules, setRewardRules] = useState([]);
  const [showRewardForm, setShowRewardForm] = useState(false);
  const [rewardType, setRewardType] = useState('weekly_rate');
  const [rewardThreshold, setRewardThreshold] = useState(5);
  const [rewardText, setRewardText] = useState('');
  // Info toggles
  const [showTypeInfo, setShowTypeInfo] = useState(false);
  const [showSetupInfo, setShowSetupInfo] = useState(false);
  const [showTriggerInfo, setShowTriggerInfo] = useState(false);

  // Load saved rhythm from localStorage when switching to Schichten
  const hasSavedRhythmus = useMemo(() => {
    try { return !!JSON.parse(userStorage.getItem(LS_SCHICHT_KEY)); } catch { return false; }
  }, []);

  const canNew = canCreateNewHabit(habits);

  // Overlap check: detect if another habit already has a calendar block at the same time+days
  const calendarConflictHabit = useMemo(() => {
    if (!calendarEnabled || frequency === 'weekly' || !reminderTime) return null;
    const [sH, sM] = reminderTime.split(':').map(Number);
    const newStart = sH * 60 + sM;
    const newEnd = newStart + (calendarDuration || 15);
    for (const h of habits) {
      if (!h.calendarReminder?.enabled) continue;
      if (h.status === 'archived' || h.status === 'paused') continue;
      const cr = h.calendarReminder;
      if (!cr.time) continue;
      const [hH, hM] = cr.time.split(':').map(Number);
      const hStart = hH * 60 + hM;
      const hEnd = hStart + (cr.durationMinutes || 15);
      if (newStart >= hEnd || hStart >= newEnd) continue;
      const hFreq = h.schedule?.frequencyType;
      const hDays = h.schedule?.daysOfWeek || [];
      let overlap = false;
      if (frequency === 'daily' && hFreq === 'daily') overlap = true;
      else if (frequency === 'daily' && hFreq === 'custom') overlap = hDays.length > 0;
      else if (frequency === 'custom' && hFreq === 'daily') overlap = daysOfWeek.length > 0;
      else if (frequency === 'custom' && hFreq === 'custom') overlap = daysOfWeek.some(d => hDays.includes(d));
      if (overlap) return h.title;
    }
    return null;
  }, [calendarEnabled, frequency, reminderTime, calendarDuration, daysOfWeek, habits]);

  // Wochenkalender-Konfliktprüfung für Nicht-Schichten
  const calendarConflictApt = useMemo(() => {
    if (!calendarEnabled || frequency === 'weekly' || !reminderTime) return null;
    const [sH, sM] = reminderTime.split(':').map(Number);
    const newStart = sH * 60 + sM;
    const newEnd = newStart + (calendarDuration || 15);
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    try {
      const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
      for (const apt of apts) {
        // Kalenderblöcke anderer Habits ebenfalls prüfen (isHabitReminder mit gültiger habitId einschließen)
        if (apt.isHabitReminder && !apt.habitId) continue;
        if (!apt.startTime || !apt.endTime) continue;
        if (apt.date && apt.date < todayStr) continue;
        const [aH, aM] = apt.startTime.split(':').map(Number);
        const [eH, eM] = apt.endTime.split(':').map(Number);
        if (newStart >= eH * 60 + eM || aH * 60 + aM >= newEnd) continue;
        if (frequency === 'custom' && daysOfWeek.length > 0) {
          const aptDate = new Date((apt.date || apt.from || '') + 'T12:00:00');
          if (isNaN(aptDate.getTime())) continue;
          if (!daysOfWeek.includes(aptDate.getDay())) continue;
        }
        return apt.title || 'Termin';
      }
    } catch {}
    return null;
  }, [calendarEnabled, frequency, reminderTime, calendarDuration, daysOfWeek]);

  // Wochenkalender-Konfliktprüfung für Schichten (pro Schicht) – nur an Tagen des jeweiligen Schichttyps laut Rhythmus
  const schichtAptConflicts = useMemo(() => {
    if (frequency !== 'schichten') return {};
    // Ohne Rhythmus keine Prüfung möglich
    if (!schichtRhythmus?.days?.length || !schichtRhythmus?.cycleLength || !schichtRhythmus?.startDate) return {};
    const result = {};
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const rhythmusStart = new Date(schichtRhythmus.startDate + 'T12:00:00').getTime();
    try {
      const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
      for (const s of SCHICHT_TYPES) {
        const z = schichtZeiten[s];
        if (!z?.calendarBlock || !z.time) continue;
        const [sH, sM] = z.time.split(':').map(Number);
        const newStart = sH * 60 + sM;
        const newEnd = newStart + (z.duration || 30);
        for (const apt of apts) {
          // Kalenderblöcke anderer Habits ebenfalls prüfen
          if (apt.isHabitReminder && !apt.habitId) continue;
          if (!apt.startTime || !apt.endTime || !apt.date) continue;
          if (apt.date < todayStr) continue;
          // Nur Termine an Tagen prüfen, an denen dieser Schichttyp laut Rhythmus aktiv ist
          const aptNoon = new Date(apt.date + 'T12:00:00').getTime();
          const offset = Math.round((aptNoon - rhythmusStart) / 86400000);
          if (offset < 0) continue;
          const pos = ((offset % schichtRhythmus.cycleLength) + schichtRhythmus.cycleLength) % schichtRhythmus.cycleLength;
          const schichtForDay = schichtRhythmus.days[pos] ?? 'Frei';
          if (schichtForDay !== s) continue; // Kein Frühschicht-/Spätschicht-/etc.-Tag → überspringen
          const [aH, aM] = apt.startTime.split(':').map(Number);
          const [eH, eM] = apt.endTime.split(':').map(Number);
          if (newStart >= eH * 60 + eM || aH * 60 + aM >= newEnd) continue;
          result[s] = apt.title || 'Termin';
          break;
        }
      }
    } catch {}
    return result;
  }, [frequency, schichtZeiten, schichtRhythmus]);

  // Dynamic steps based on type
  const steps = useMemo(() => {
    if (type === 'performance') return ['type', 'define', 'setup', 'trigger', 'reward'];
    return ['type', 'define', 'trigger', 'reward'];
  }, [type]);

  const currentStep = steps[stepIdx] || 'type';
  const totalSteps = steps.length;
  const isLast = stepIdx === totalSteps - 1;

  const isValid = currentStep === 'type' ? type !== null
    : currentStep === 'define'
      ? title.trim().length > 0 && !calendarConflictHabit &&
        (!calendarConflictApt || conflictsFullyResolved) &&
        (Object.keys(schichtAptConflicts).length === 0 || Object.keys(schichtAptConflicts).every(s => resolvedShifts.has(s))) &&
        (frequency !== 'schichten' || schichtRhythmus !== null)
    : true;

  const toggleDay = (day) => {
    setDaysOfWeek(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const goNext = () => {
    if (currentStep === 'type' && type !== 'performance' && stepIdx === 0) {
      // Recalculate steps after type selection
      setStepIdx(1);
    } else {
      setStepIdx(stepIdx + 1);
    }
  };

  const goBack = () => setStepIdx(Math.max(0, stepIdx - 1));

  const handleSave = () => {
    // 1. Pending Termin-Änderungen aus Konfliktauflösung jetzt in localStorage schreiben
    if (Object.keys(pendingAptChanges.updates).length > 0 || pendingAptChanges.deletes.length > 0) {
      try {
        const DELETED_KEY = 'zielplanung_deleted_habit_reminders';
        const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
        const deletedHrIds = JSON.parse(userStorage.getItem(DELETED_KEY) || '[]');
        const newDeletedHrIds = new Set(deletedHrIds);
        const getKey = a => a.id != null ? String(a.id) : `${a.date}||${a.startTime}`;
        const newApts = [];

        for (const apt of apts) {
          const k = getKey(apt);
          if (pendingAptChanges.deletes.includes(k)) {
            // Gelöschter Termin: wenn Habit-Reminder → auch aus Regenerierung ausschließen
            if (apt.isHabitReminder && typeof apt.id === 'string' && apt.id.startsWith('hr_')) {
              newDeletedHrIds.add(apt.id);
            }
            continue; // aus Liste entfernen
          }
          if (pendingAptChanges.updates[k]) {
            const upd = pendingAptChanges.updates[k];
            if (apt.isHabitReminder && typeof apt.id === 'string' && apt.id.startsWith('hr_')) {
              // Verschobener Habit-Reminder: Original löschen + neuen regulären Termin anlegen
              newDeletedHrIds.add(apt.id);
              newApts.push({
                ...upd,
                id: `moved_hr_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                isHabitReminder: false,
                habitId: undefined,
              });
            } else {
              newApts.push({ ...apt, ...upd });
            }
            continue;
          }
          newApts.push(apt);
        }

        userStorage.setItem('zielplanung_appointments', JSON.stringify(newApts));
        userStorage.setItem(DELETED_KEY, JSON.stringify([...newDeletedHrIds]));
        window.dispatchEvent(new CustomEvent('burningteam-calendar-sync'));
      } catch (e) {
        console.error('Fehler beim Schreiben der Termin-Änderungen:', e);
      }
    }
    // 2. Habit speichern
    const habit = {
      id: `habit_${Date.now()}`,
      title: title.trim(),
      type,
      status: 'new',
      startDate: getLocalDateStr(),
      skippedDates: pendingSkippedDates.length > 0 ? [...pendingSkippedDates] : undefined,
      dailyTimeOverrides: Object.keys(pendingDailyTimeOverrides).length > 0 ? { ...pendingDailyTimeOverrides } : undefined,
      schedule: {
        frequencyType: frequency,
        daysOfWeek: frequency === 'custom' ? daysOfWeek : [],
        weeklyTargetCount: frequency === 'weekly' ? weeklyTarget : null,
        schichtRhythmus: frequency === 'schichten' ? schichtRhythmus : null,
        schichtZeiten: frequency === 'schichten' ? schichtZeiten : null,
      },
      ifThen: { cuePreset: null, cueCustom: cue || null, actionPhrase: null },
      tracking: type === 'performance' ? {
        unitLabel: unitLabel || 'mal',
        minimumTarget,
        currentTarget: currentTarget || minimumTarget,
        goalTarget: goalTarget ? Number(goalTarget) : null,
        progressionMode: 'manual'
      } : null,
      rewards: { bigRewardsEnabled: rewardRules.length > 0, bigRewardRules: rewardRules },
      gamification: { xpPerCompletion: 10 },
      notifications: { reminderEnabled, reminderTime: reminderEnabled ? reminderTime : null, recoveryNudgeEnabled: true },
      calendarReminder: {
        enabled: frequency !== 'weekly' && calendarEnabled,
        time: reminderTime,
        consolidationMonths: consolidationMonths || 3,
        durationMinutes: calendarDuration || 15
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    onSave(habit);
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <button onClick={goBack} className="p-1 text-slate-400 hover:text-slate-600">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <span className="text-sm text-slate-500">Schritt {stepIdx + 1}/{totalSteps}</span>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* STEP: TYPE */}
          {currentStep === 'type' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Was für ein Habit?</h2>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => setType('binary')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    type === 'binary' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="type-binary"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type === 'binary' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Einfach erledigen</p>
                      <p className="text-xs text-slate-500">Ja/Nein-Habit: 1h Business, Restorate einnehmen, Joggen</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setType('performance')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    type === 'performance' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="type-performance"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type === 'performance' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Zählen & steigern</p>
                      <p className="text-xs text-slate-500">Messbares Habit mit 1%-Methode: Liegestütze, Minuten, Seiten</p>
                    </div>
                  </div>
                </button>
              </div>
              <button
                onClick={() => setShowTypeInfo(!showTypeInfo)}
                className="flex items-center gap-1.5 text-slate-400 hover:text-emerald-500 transition-colors"
                data-testid="type-info-toggle"
              >
                <Info className="w-3.5 h-3.5" />
                <span className="text-xs">{showTypeInfo ? 'Info ausblenden' : 'Was ist der Unterschied?'}</span>
              </button>
              {showTypeInfo && (
                <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-500 animate-in fade-in">
                  <strong>Einfach erledigen:</strong> Du hast es gemacht oder nicht (z.B. 1h Business gearbeitet).<br />
                  <strong>Zählen & steigern:</strong> Du trackst einen Wert und steigerst ihn schrittweise mit der 1%-Methode (z.B. heute 10 Liegestütze, nächste Woche 12).
                </div>
              )}
            </>
          )}

          {/* STEP: DEFINE */}
          {currentStep === 'define' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Habit definieren</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Titel</label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="z.B. 1h Business"
                    className="w-full mt-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="habit-title-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Wie oft?</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {[
                      { key: 'daily',    label: 'Täglich' },
                      { key: 'weekly',   label: 'X pro Woche' },
                      { key: 'custom',   label: 'Bestimmte Tage' },
                      { key: 'schichten', label: 'Schichten', icon: <Layers className="w-3 h-3" /> }
                    ].map(f => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => {
                          setFrequency(f.key);
                          // Load saved rhythm if switching to Schichten
                          if (f.key === 'schichten' && !schichtRhythmus && hasSavedRhythmus) {
                            try {
                              const saved = JSON.parse(userStorage.getItem(LS_SCHICHT_KEY));
                              if (saved) setSchichtRhythmus(saved);
                            } catch {}
                          }
                        }}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          frequency === f.key
                            ? f.key === 'schichten'
                              ? 'bg-violet-600 text-white'
                              : 'bg-emerald-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {f.icon}{f.label}
                      </button>
                    ))}
                  </div>
                </div>
                {frequency === 'weekly' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={7} value={weeklyTarget}
                      onChange={e => setWeeklyTarget(Number(e.target.value))}
                      className="w-16 px-2 py-1.5 border rounded-lg text-sm text-center"
                    />
                    <span className="text-sm text-slate-500">mal pro Woche</span>
                  </div>
                )}
                {frequency === 'custom' && (
                  <div className="flex gap-1.5">
                    {WEEKDAY_LABELS.map((d, i) => (
                      <button
                        key={i}
                        onClick={() => toggleDay(i)}
                        className={`w-9 h-9 rounded-lg text-xs font-medium transition-colors ${
                          daysOfWeek.includes(i) ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                )}

                {/* ── Schichten ── */}
                {frequency === 'schichten' && (
                  <div className="space-y-3">
                    {/* Rhythmus-Block */}
                    <div className={`rounded-xl border p-3 ${schichtRhythmus ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}>
                      {schichtRhythmus ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-violet-800">
                              Rhythmus: {schichtRhythmus.cycleLength} Tage
                            </p>
                            <p className="text-[10px] text-violet-600 mt-0.5">
                              {Array.from(new Set(schichtRhythmus.days)).join(' · ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setShowVorschauModal(true)}
                              className="text-xs px-2.5 py-1 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors font-medium"
                            >
                              Vorschau
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowSchichtModal(true)}
                              className="text-xs px-2.5 py-1 rounded-lg bg-violet-200 text-violet-800 hover:bg-violet-300 transition-colors font-medium"
                            >
                              Ändern
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowSchichtModal(true)}
                          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-violet-600 font-semibold hover:text-violet-800 transition-colors"
                        >
                          <Layers className="w-4 h-4" />
                          Schichtrhythmus einrichten →
                        </button>
                      )}
                    </div>

                    {/* Schicht-Zeiten – komplett neu */}
                    {schichtRhythmus && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-600">An welchen Schichten?</p>
                        {/* Shift-Selector */}
                        <div className="grid grid-cols-2 gap-2">
                          {SCHICHT_TYPES.map(s => {
                            const isInRhythm = s === 'Frei'
                              ? schichtRhythmus.days.includes(null)
                              : schichtRhythmus.days.includes(s);
                            const z = schichtZeiten[s] || {};
                            return (
                              <button key={s} type="button"
                                disabled={!isInRhythm}
                                onClick={() => setSchichtZeiten(prev => ({
                                  ...prev,
                                  [s]: { ...(DEFAULT_ZEITEN[s]), ...(prev[s] || {}), enabled: !prev[s]?.enabled }
                                }))}
                                className={`px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
                                  !isInRhythm
                                    ? 'opacity-30 cursor-not-allowed bg-slate-50 border-slate-200 text-slate-400'
                                    : z.enabled
                                      ? SCHICHT_BTN_ACTIVE[s]
                                      : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
                                }`}
                              >
                                {s}schicht
                              </button>
                            );
                          })}
                        </div>

                        {/* Per-shift options */}
                        {SCHICHT_TYPES.filter(s => schichtZeiten[s]?.enabled).map(s => {
                          const z = schichtZeiten[s] || {};
                          // Positions in cycle for this schicht type
                          const cyclePositions = schichtRhythmus.days
                            .map((d, i) => ({ d: d ?? 'Frei', i }))
                            .filter(({ d }) => d === s);

                          return (
                            <div key={s} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                              <p className="text-xs font-semibold text-slate-700">{s}schicht</p>

                              {/* Alle / Bestimmte */}
                              <div className="flex gap-1.5">
                                {['alle', 'bestimmte'].map(t => (
                                  <button key={t} type="button"
                                    onClick={() => setSchichtZeiten(prev => ({
                                      ...prev, [s]: { ...prev[s], type: t,
                                        enabledCyclePositions: t === 'bestimmte' ? cyclePositions.map(c => c.i) : []
                                      }
                                    }))}
                                    className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold border transition-colors ${
                                      z.type === t
                                        ? 'bg-violet-100 border-violet-300 text-violet-800'
                                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
                                    }`}
                                  >
                                    {t === 'alle' ? 'Alle' : 'Bestimmte'}
                                  </button>
                                ))}
                              </div>

                              {/* Bestimmte: Button öffnet SchichtTagsModal */}
                              {z.type === 'bestimmte' && cyclePositions.length > 0 && (
                                <div className="flex items-center gap-2">
                                  <button type="button"
                                    onClick={() => { setSchichtTagsTargetSchicht(s); setShowSchichtTagsModal(true); }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-100 border border-violet-300 text-violet-800 text-xs font-semibold hover:bg-violet-200 transition-colors"
                                  >
                                    <Layers className="w-3 h-3" />
                                    Tage auswählen
                                    {(z.enabledCyclePositions || []).length > 0 && (
                                      <span className="bg-violet-600 text-white rounded-full px-1.5 py-0.5 text-[9px] leading-none">
                                        {(z.enabledCyclePositions || []).length}/{cyclePositions.length}
                                      </span>
                                    )}
                                  </button>
                                </div>
                              )}

                              {/* Kalender-Block toggle */}
                              <div className="flex items-center justify-between pt-1 border-t border-slate-200">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3 h-3 text-slate-400" />
                                  <span className="text-[10px] text-slate-500">Kalender-Block hinzufügen</span>
                                </div>
                                <button type="button"
                                  onClick={() => setSchichtZeiten(prev => ({
                                    ...prev, [s]: { ...prev[s], calendarBlock: !prev[s]?.calendarBlock }
                                  }))}
                                  className={`w-8 h-4 rounded-full transition-colors ${z.calendarBlock ? 'bg-blue-500' : 'bg-slate-200'}`}
                                >
                                  <div className={`w-3 h-3 bg-white rounded-full shadow transform transition-transform ${z.calendarBlock ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                </button>
                              </div>

                              {/* Time + Duration + Months */}
                              {z.calendarBlock && (
                                <div className="space-y-1.5 p-2 bg-blue-50 rounded-lg border border-blue-100">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 w-12">Uhrzeit</span>
                                    <input type="time" value={z.time || '08:00'}
                                      onChange={e => { setSchichtZeiten(prev => ({ ...prev, [s]: { ...prev[s], time: e.target.value } })); setResolvedShifts(prev => { const n = new Set(prev); n.delete(s); return n; }); }}
                                      className="px-2 py-0.5 border border-slate-200 rounded text-xs bg-white" />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 w-12">Dauer</span>
                                    <input type="number" min={5} max={240} step={5} value={z.duration || 30}
                                      onChange={e => setSchichtZeiten(prev => ({ ...prev, [s]: { ...prev[s], duration: Number(e.target.value) } }))}
                                      className="w-14 px-2 py-0.5 border border-slate-200 rounded text-xs text-center bg-white" />
                                    <span className="text-[10px] text-slate-400">Min.</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 w-12">Zeitraum</span>
                                    <input type="number" min={1} max={24} value={z.months || 3}
                                      onChange={e => setSchichtZeiten(prev => ({ ...prev, [s]: { ...prev[s], months: Number(e.target.value) } }))}
                                      className="w-14 px-2 py-0.5 border border-slate-200 rounded text-xs text-center bg-white" />
                                    <span className="text-[10px] text-slate-400">Mon.</span>
                                  </div>
                                  {schichtAptConflicts[s] && !resolvedShifts.has(s) && (
                                    <div className="flex items-center gap-1 p-1.5 bg-red-50 rounded border border-red-200">
                                      <Info className="w-3 h-3 text-red-500 flex-shrink-0" />
                                      <p className="text-[10px] text-red-600 flex-1">Konflikt mit „{schichtAptConflicts[s]}"</p>
                                      <button type="button"
                                        onClick={() => { setConflictModalForShift(s); setShowConflictModal(true); }}
                                        className="flex-shrink-0 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg shadow-sm transition-colors"
                                        data-testid={`conflict-solve-${s}`}>
                                        Lösen
                                      </button>
                                    </div>
                                  )}
                                  {schichtAptConflicts[s] && resolvedShifts.has(s) && (
                                    <div className="flex items-center gap-1 p-1.5 bg-green-50 rounded border border-green-200">
                                      <span className="text-[10px] text-green-700 font-semibold">✓ Konflikte gelöst</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {/* Kalender-Block hinzufügen – nur für nicht-Schichten Frequenzen */}
                {frequency !== 'schichten' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className={`text-sm ${frequency === 'weekly' ? 'text-slate-300' : 'text-slate-600'}`}>Kalender-Block hinzufügen</span>
                      {frequency === 'weekly' && (
                        <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">nur tägl./best. Tage</span>
                      )}
                    </div>
                    <button
                      onClick={() => frequency !== 'weekly' && setCalendarEnabled(!calendarEnabled)}
                      disabled={frequency === 'weekly'}
                      className={`w-10 h-6 rounded-full transition-colors ${frequency === 'weekly' ? 'bg-slate-100 cursor-not-allowed' : calendarEnabled ? 'bg-blue-500' : 'bg-slate-200'}`}
                      data-testid="calendar-reminder-toggle"
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${calendarEnabled && frequency !== 'weekly' ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {calendarEnabled && frequency !== 'weekly' && (
                    <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-14">Uhrzeit</span>
                        <input type="time" value={reminderTime} onChange={e => { setReminderTime(e.target.value); setConflictsFullyResolved(false); }}
                          className="px-2 py-1 border border-slate-200 rounded-lg text-sm bg-white" data-testid="calendar-reminder-time" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-14">Dauer</span>
                        <input type="number" min={5} max={240} step={5} value={calendarDuration}
                          onChange={e => setCalendarDuration(e.target.value === '' ? '' : Number(e.target.value))}
                          onClick={e => e.target.select()}
                          onBlur={() => { if (!calendarDuration || calendarDuration < 5) setCalendarDuration(5); }}
                          className="w-16 px-2 py-1 border border-slate-200 rounded-lg text-sm text-center bg-white" data-testid="calendar-duration" />
                        <span className="text-xs text-slate-400">Min.</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 w-14">Zeitraum</span>
                          <input type="number" min={1} max={24} value={consolidationMonths}
                            onChange={e => setConsolidationMonths(e.target.value === '' ? '' : Number(e.target.value))}
                            onClick={e => e.target.select()}
                            onBlur={() => { if (!consolidationMonths || consolidationMonths < 1) setConsolidationMonths(3); }}
                            className="w-16 px-2 py-1 border border-slate-200 rounded-lg text-sm text-center bg-white" data-testid="calendar-consolidation" />
                          <span className="text-xs text-slate-400">Monate</span>
                          <button onClick={() => setShowConsolidationInfo(!showConsolidationInfo)} className="text-slate-300 hover:text-blue-400 transition-colors" type="button">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {showConsolidationInfo && (
                          <p className="text-[10px] text-slate-500 bg-white rounded p-2 border border-blue-100 leading-relaxed">
                            Laut Forschung dauert es im Schnitt <strong>etwa 66 Tage</strong> (ca. 2–3 Monate), bis eine neue Routine wirklich verinnerlicht ist. Blockiere dir diese Zeit bewusst im Kalender.
                          </p>
                        )}
                      </div>
                      {calendarConflictHabit && (
                        <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                          <Info className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-[10px] text-red-600">
                            Zeitkonflikt mit Habit <strong>„{calendarConflictHabit}"</strong>. Bitte wähle eine andere Uhrzeit oder Dauer.
                          </p>
                        </div>
                      )}
                      {calendarConflictApt && !calendarConflictHabit && (
                        conflictsFullyResolved ? (
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                            <span className="text-[10px] text-green-700 font-semibold">✓ Konflikte gelöst</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                            <Info className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                            <p className="text-[10px] text-red-600 flex-1">
                              Konflikt mit Termin <strong>„{calendarConflictApt}"</strong>.
                            </p>
                            <button type="button"
                              onClick={() => { setConflictModalForShift(null); setShowConflictModal(true); }}
                              className="flex-shrink-0 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg shadow-sm transition-colors"
                              data-testid="conflict-solve-apt">
                              Lösen
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                )}
                {!canNew && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      Studien zeigen: Max. 3 neue Routinen gleichzeitig aufzubauen ist am effektivsten.
                      Du hast bereits 3 im Aufbau. Du kannst trotzdem weitere anlegen, aber fokussiere dich zuerst auf deine bestehenden.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* STEP: 1% SETUP (Performance only) */}
          {currentStep === 'setup' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">1%-Setup</h2>
              <div className="space-y-4">
                <button
                  onClick={() => setShowSetupInfo(!showSetupInfo)}
                  className="flex items-center gap-1.5 text-slate-400 hover:text-blue-500 transition-colors"
                  data-testid="setup-info-toggle"
                >
                  <Info className="w-3.5 h-3.5" />
                  <span className="text-xs">{showSetupInfo ? 'Info ausblenden' : 'Was ist die 1%-Methode?'}</span>
                </button>
                {showSetupInfo && (
                  <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-600 animate-in fade-in">
                    <strong>1%-Methode:</strong> Starte mit dem absoluten Minimum (z.B. 1 Liegestütz) und steigere langsam.
                    Das „Minimum" ist deine kleinste Version, die du auch an schlechten Tagen schaffst.
                    Das „Aktuelle Ziel" ist dein normales Tagesziel.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Einheit</label>
                    <input type="text" value={unitLabel} onChange={e => setUnitLabel(e.target.value)}
                      placeholder="z.B. Stk, min, Seiten" className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Endziel (optional)</label>
                    <input type="number" value={goalTarget} onChange={e => setGoalTarget(e.target.value)}
                      placeholder="z.B. 100" className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Minimum (1%-Version)</label>
                    <input type="number" min={1} value={minimumTarget}
                      onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); setMinimumTarget(v); if (currentTarget < Number(v)) setCurrentTarget(Number(v)); }}
                      onClick={e => e.target.select()}
                      onBlur={() => { if (!minimumTarget || minimumTarget < 1) setMinimumTarget(1); }}
                      className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Kleinste Version, die immer geht</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Aktuelles Tagesziel</label>
                    <input type="number" min={minimumTarget} value={currentTarget}
                      onChange={e => setCurrentTarget(e.target.value === '' ? '' : Number(e.target.value))}
                      onClick={e => e.target.select()}
                      onBlur={() => { if (!currentTarget || currentTarget < minimumTarget) setCurrentTarget(minimumTarget || 1); }}
                      className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Dein normales Ziel pro Tag</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* STEP: TRIGGER */}
          {currentStep === 'trigger' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Trigger setzen</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Wann machst du es? (If-Then)</label>
                  <button
                    onClick={() => setShowTriggerInfo(!showTriggerInfo)}
                    className="flex items-center gap-1.5 text-slate-400 hover:text-emerald-500 transition-colors mt-1"
                    data-testid="trigger-info-toggle"
                  >
                    <Info className="w-3.5 h-3.5" />
                    <span className="text-xs">{showTriggerInfo ? 'Info ausblenden' : 'Warum ein Trigger?'}</span>
                  </button>
                  {showTriggerInfo && (
                    <p className="text-xs text-slate-400 mt-1.5 p-2.5 bg-slate-50 rounded-lg animate-in fade-in">
                      Wähle einen festen Auslöser in deinem Alltag. Je klarer der Moment, desto leichter wird die Gewohnheit.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {CUE_PRESETS.map(c => (
                      <button key={c} onClick={() => setCue(cue === c ? '' : c)}
                        className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                          cue === c ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}>
                        {c}
                      </button>
                    ))}
                  </div>
                  <input type="text" value={CUE_PRESETS.includes(cue) ? '' : cue}
                    onChange={e => setCue(e.target.value)} placeholder="Oder eigenen Trigger eingeben..."
                    className="w-full mt-2 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
            </>
          )}

          {/* STEP: REWARD */}
          {currentStep === 'reward' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Belohnung</h2>
              <div className="flex items-start gap-2 p-3 bg-purple-50 rounded-lg">
                <Gift className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-purple-600">
                  Belohne dich für Fortschritte! Setze dir Meilensteine und definiere, was du dir gönnst.
                  Du kannst diesen Schritt auch überspringen.
                </p>
              </div>

              {rewardRules.length > 0 && (
                <div className="space-y-2">
                  {rewardRules.map((rule, i) => (
                    <div key={i} className="p-3 rounded-lg border border-purple-200 bg-purple-50/50 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500">
                          {rule.ruleType === 'weekly_rate' ? `${rule.threshold}x pro Woche` :
                           rule.ruleType === 'streak' ? `${rule.threshold} Tage Streak` :
                           `${rule.threshold}x insgesamt`}
                        </p>
                        <p className="text-sm font-medium text-purple-700">{rule.rewardText}</p>
                      </div>
                      <button onClick={() => setRewardRules(rewardRules.filter((_, idx) => idx !== i))}
                        className="p-1 text-slate-300 hover:text-red-400" data-testid={`remove-reward-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showRewardForm ? (
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div className="flex gap-1.5">
                    {[
                      { key: 'weekly_rate', label: 'Pro Woche' },
                      { key: 'streak', label: 'Streak' },
                      { key: 'total_completions', label: 'Insgesamt' }
                    ].map(t => (
                      <button key={t.key} onClick={() => setRewardType(t.key)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          rewardType === t.key ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 border border-slate-200'
                        }`} data-testid={`reward-type-${t.key}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Wenn</span>
                    <input type="number" min={1} value={rewardThreshold}
                      onChange={e => setRewardThreshold(Number(e.target.value))}
                      className="w-14 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center" data-testid="reward-threshold" />
                    <span className="text-xs text-slate-500">
                      {rewardType === 'weekly_rate' ? 'x diese Woche' : rewardType === 'streak' ? 'Tage am Stück' : 'x insgesamt'}
                    </span>
                  </div>
                  <input type="text" value={rewardText} onChange={e => setRewardText(e.target.value)}
                    placeholder="Belohnung: z.B. Netflix-Abend, Essen gehen"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="reward-text-input" />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (!rewardText.trim()) return;
                        setRewardRules([...rewardRules, { ruleType: rewardType, threshold: rewardThreshold, rewardText: rewardText.trim() }]);
                        setRewardText(''); setRewardThreshold(5); setShowRewardForm(false);
                      }}
                      disabled={!rewardText.trim()}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                        rewardText.trim() ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`} data-testid="reward-add-btn">
                      Hinzufügen
                    </button>
                    <button onClick={() => { setShowRewardForm(false); setRewardText(''); }}
                      className="px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm rounded-lg">
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowRewardForm(true)}
                  className="w-full py-3 border-2 border-dashed border-purple-200 rounded-xl text-sm font-medium text-purple-400 hover:border-purple-400 hover:text-purple-600 flex items-center justify-center gap-2 transition-colors"
                  data-testid="add-reward-btn">
                  <Plus className="w-4 h-4" /> Belohnung hinzufügen
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 space-y-2">
          {/* Konflikt-Banner im Footer – immer sichtbar, unabhängig vom Scroll-Status */}
          {currentStep === 'define' && calendarEnabled && calendarConflictHabit && (
            <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
              <Info className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-600">Zeitkonflikt mit Habit <strong>„{calendarConflictHabit}"</strong>. Bitte andere Uhrzeit wählen.</p>
            </div>
          )}
          {currentStep === 'define' && calendarEnabled && calendarConflictApt && !calendarConflictHabit && (
            conflictsFullyResolved ? (
              <div className="flex items-center gap-1.5 p-2 bg-green-50 rounded-lg border border-green-200">
                <span className="text-xs text-green-700 font-semibold">✓ Konflikte gelöst – Weiter möglich</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                <Info className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600 flex-1">Konflikt mit Termin <strong>„{calendarConflictApt}"</strong></p>
                <button type="button"
                  onClick={() => { setConflictModalForShift(null); setShowConflictModal(true); }}
                  className="flex-shrink-0 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg shadow-sm transition-colors"
                  data-testid="footer-conflict-solve-apt">
                  Lösen
                </button>
              </div>
            )
          )}
          {currentStep === 'define' && frequency === 'schichten' && Object.entries(schichtAptConflicts).map(([s, aptTitle]) =>
            !resolvedShifts.has(s) ? (
              <div key={s} className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                <Info className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600 flex-1">{s}schicht: Konflikt mit „{aptTitle}"</p>
                <button type="button"
                  onClick={() => { setConflictModalForShift(s); setShowConflictModal(true); }}
                  className="flex-shrink-0 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg shadow-sm transition-colors"
                  data-testid={`footer-conflict-solve-${s}`}>
                  Lösen
                </button>
              </div>
            ) : null
          )}
          {!isLast ? (
            <button onClick={goNext} disabled={!isValid}
              className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
                isValid ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`} data-testid="wizard-next">
              Weiter <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSave}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              data-testid="wizard-save">
              <Check className="w-5 h-5" /> Habit erstellen
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Kalender-Konflikt Modal */}
    {showConflictModal && (
      <HabitCalendarConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onDone={({ globalTime, globalDuration, skippedDates, dailyTimeOverrides, aptUpdates = {}, aptDeletes = [] }) => {
          if (!conflictModalForShift) {
            if (globalTime) setReminderTime(globalTime);
            if (globalDuration) setCalendarDuration(globalDuration);
          } else {
            // Schicht: Zeit für diese Schicht übernehmen + als gelöst markieren
            if (globalTime) setSchichtZeiten(prev => ({ ...prev, [conflictModalForShift]: { ...prev[conflictModalForShift], time: globalTime } }));
            if (globalDuration) setSchichtZeiten(prev => ({ ...prev, [conflictModalForShift]: { ...prev[conflictModalForShift], duration: globalDuration } }));
            setResolvedShifts(prev => new Set([...prev, conflictModalForShift]));
          }
          if (skippedDates?.length > 0) setPendingSkippedDates(prev => [...prev, ...skippedDates]);
          if (dailyTimeOverrides && Object.keys(dailyTimeOverrides).length > 0) {
            setPendingDailyTimeOverrides(prev => ({ ...prev, ...dailyTimeOverrides }));
          }
          // Termin-Änderungen sammeln (werden erst bei "Habit erstellen" geschrieben)
          if (Object.keys(aptUpdates).length > 0 || aptDeletes.length > 0) {
            setPendingAptChanges(prev => ({
              updates: { ...prev.updates, ...aptUpdates },
              deletes: [...new Set([...prev.deletes, ...aptDeletes])],
            }));
          }
          setConflictsFullyResolved(true);
          setShowConflictModal(false);
        }}
        frequency={conflictModalForShift ? 'schichten' : frequency}
        habitTitle={title || undefined}
        habitId={undefined}
        schichtRhythmus={conflictModalForShift ? schichtRhythmus : undefined}
        habitTime={conflictModalForShift ? null : reminderTime}
        habitDuration={conflictModalForShift ? null : calendarDuration}
        daysOfWeek={daysOfWeek}
        schichtZeiten={conflictModalForShift
          ? { [conflictModalForShift]: schichtZeiten[conflictModalForShift] }
          : undefined}
      />
    )}

    {/* Schicht-Rhythmus Modal (rendered outside main modal for z-index) */}
    {showSchichtModal && (
      <SchichtRhythmusModal
        existingRhythmus={schichtRhythmus}
        onSave={(rhythmus, usedTypes) => {
          setSchichtRhythmus(rhythmus);
          setSchichtZeiten(prev => {
            const next = { ...DEFAULT_ZEITEN };
            Object.keys(prev).forEach(s => {
              next[s] = usedTypes.includes(s) ? (prev[s] || '') : '';
            });
            return next;
          });
          setShowSchichtModal(false);
        }}
        onClose={() => setShowSchichtModal(false)}
      />
    )}

    {/* Schicht-Tags Modal – zeigt nur Tage des gewählten Schichttyps */}
    {showSchichtTagsModal && schichtTagsTargetSchicht && schichtRhythmus && (
      <SchichtTagsModal
        rhythmus={schichtRhythmus}
        targetSchicht={schichtTagsTargetSchicht}
        selectedPositions={schichtZeiten[schichtTagsTargetSchicht]?.enabledCyclePositions || []}
        onSave={(positions) => {
          setSchichtZeiten(prev => ({
            ...prev,
            [schichtTagsTargetSchicht]: { ...prev[schichtTagsTargetSchicht], enabledCyclePositions: positions }
          }));
          setShowSchichtTagsModal(false);
        }}
        onClose={() => setShowSchichtTagsModal(false)}
      />
    )}

    {/* Schicht-Vorschau Modal – zeigt nächste 8 Wochen */}
    {showVorschauModal && schichtRhythmus && (
      <SchichtVorschauModal
        rhythmus={schichtRhythmus}
        schichtZeiten={schichtZeiten}
        onClose={() => setShowVorschauModal(false)}
      />
    )}
    </>
  );
}
